import React, { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import { deriveMapStateFromConfig, TWC } from '@tmapy/beetlejuice-utils';
import { useMessage } from '@tmapy/intl';
import type { GeoJSONFeature } from '@tmapy/mapcore';
import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  Dialog,
  FormItem,
  FormRow,
  InfoAlert,
  Link,
  SecondaryBtn,
  WaitingAlert,
} from '@tmapy/style-guide';

import './EpsiOrderDetail.scss';

import { EpsiOrderApproveStateEnum } from '../../openapi/types/EpsiOrderApproveStateEnum';
import { epsiOrdersApprovePartialUpdate } from '../../openapi/operations/epsiOrdersApprovePartialUpdate';
import {
  epsiOrdersRetrieveOperation,
  useEpsiOrdersRetrieveResource,
} from '../../openapi/operations/epsiOrdersRetrieve';

import { QUERY_STATE } from '../../constants';
import { useDownloadLink } from '../../hooks';
import { MainContent } from '../../app/components/MainContent';
import { MapWrapper } from '../../app/components/MapWrapper';
import { PageTitle } from '../../app/components/PageTitle';
import { UNMANAGED_AREA_LAYERS } from '../../areas/constants';
import { mapConfig } from '../../areas/mapConfig';
import { MapWithAreas } from '../../areas/components/MapWithAreas';
import { DocDownloadFiles } from '../../docs/components/DocDownloadFiles';
import { OrderBadge } from '../../orders/components/OrderBadge';
import { RejectionDialog } from '../../orders/components/RejectionDialog';
import type { ORDER_STATE } from '../../orders/constants';

import {
  epsiOrderMessages as msg,
  pspiObsahMessages,
  statusZadateleMessages,
} from './epsiOrderMessages';

export type EpsiOrderDetailProps = {
  orderId: string;
};

/**
 * `uzemni_vymezeni` chodí z BE jako GeoJSON MultiPolygon serializovaný do stringu,
 * v souřadnicích S-JTSK (EPSG:5514) – tedy ve stejné projekci, v jaké jede mapa,
 * nepřepočítává se. Mapě se předává zabalený do Feature.
 */
const toAreaFeatures = (uzemniVymezeni: string | null | undefined): GeoJSONFeature[] => {
  if (!uzemniVymezeni) {
    return [];
  }

  try {
    const geometry = JSON.parse(uzemniVymezeni);
    return [
      {
        type: 'Feature',
        geometry,
        properties: { layerId: UNMANAGED_AREA_LAYERS.PREDEFINED_AREAS },
      } as GeoJSONFeature,
    ];
  } catch (error) {
    console.error('EpsiOrderDetail: uzemni_vymezeni není platný GeoJSON', error);
    return [];
  }
};

export const EpsiOrderDetail: React.FC<EpsiOrderDetailProps> = ({ orderId }) => {
  const formatMessage = useMessage();
  const id = parseInt(orderId, 10);

  const [isRejectionDialogVisible, setRejectionDialogVisibility] = useState(false);
  const [isApprovalDialogVisible, setApprovalDialogVisibility] = useState(false);
  const [approveQueryState, setApproveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const resource = useEpsiOrdersRetrieveResource(id ? { id } : null);
  const { queryState, data } = resource;

  const mapState = useMemo(() => deriveMapStateFromConfig({ map: mapConfig() } as TWC), []);
  const areaFeatures = useMemo(
    () => toAreaFeatures(data?.uzemni_vymezeni),
    [data?.uzemni_vymezeni],
  );

  const orderUrl = id
    ? `${globalThis.DMX_CONFIG.server}${epsiOrdersRetrieveOperation({ id }).path}`
    : '';
  const prilohaLink = useDownloadLink(orderUrl, null, 'download_priloha');

  const handleDialogClose = useCallback(() => {
    setRejectionDialogVisibility(false);
    setApprovalDialogVisibility(false);
  }, []);

  const approve = useCallback(
    async (request: { state: EpsiOrderApproveStateEnum; rejection_reason?: string }) => {
      handleDialogClose();
      setApproveQueryState(QUERY_STATE.WAITING);

      const response = await epsiOrdersApprovePartialUpdate({ query: { id }, request });

      if (!response.error && response.ok) {
        setApproveQueryState(QUERY_STATE.SUCCESS);
        resource.refetch();
      } else {
        setApproveQueryState(QUERY_STATE.FAIL);
      }
    },
    [handleDialogClose, id, resource],
  );

  const handleApprovalConfirm = useCallback(
    () => approve({ state: EpsiOrderApproveStateEnum.APPROVED }),
    [approve],
  );

  const handleRejectionConfirm = useCallback(
    (rejection_reason: string) =>
      approve({ state: EpsiOrderApproveStateEnum.REJECTED, rejection_reason }),
    [approve],
  );

  const canApprove = !!data?.can_approve && approveQueryState !== QUERY_STATE.WAITING;

  return (
    <MainContent width='full'>
      <article className='sg-u-vs-4'>
        <div className='dmx-epsiOrder--head sg-u-vs-2'>
          <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
            <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
              <PageTitle>
                <FormattedMessage {...msg.detailTitle} values={{ id }} />
              </PageTitle>
              {data?.state && <OrderBadge state={data.state as ORDER_STATE} />}
            </header>
            {canApprove && (
              <BtnBar>
                <CtaBtn onClick={() => setApprovalDialogVisibility(true)}>
                  <FormattedMessage {...msg.approveBtn} />
                </CtaBtn>
                <SecondaryBtn isDanger onClick={() => setRejectionDialogVisibility(true)}>
                  <FormattedMessage {...msg.rejectBtn} />
                </SecondaryBtn>
              </BtnBar>
            )}
          </div>

          {queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.detailStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.detailStateFail} />
            </DangerAlert>
          )}
          {approveQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.approveFail} />
            </DangerAlert>
          )}
        </div>

        {data && (
          <div className='dmx-epsiOrder--cols'>
            <div className='sg-u-vs-4'>
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...msg.sectionRequest} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-u-vs-1 sg-a-mt-1'>
                  <FormRow>
                    <FormItem label={formatMessage(msg.columnIdPodani)}>
                      {data.dmvs_id_podani}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.columnPspiObsah)}>
                      {data.pspi_obsah && formatMessage.lookup(pspiObsahMessages, data.pspi_obsah)}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.columnCreated)}>
                      {data.created && (
                        <time dateTime={data.created}>
                          <FormattedDate value={new Date(data.created)} />{' '}
                          <FormattedTime value={new Date(data.created)} />
                        </time>
                      )}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.columnDatumPlatnosti)}>
                      {data.datum_platnosti && (
                        <time dateTime={data.datum_platnosti}>
                          <FormattedDate value={new Date(data.datum_platnosti)} />
                        </time>
                      )}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.pravniPredpisKod)}>
                      {data.pravni_predpis_kod}
                    </FormItem>
                  </FormRow>
                </div>
              </section>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...msg.sectionPurpose} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-u-vs-1 sg-a-mt-1'>
                  <FormRow>
                    <FormItem label={formatMessage(msg.ucelKod)}>{data.ucel_kod}</FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.ucelTextem)}>{data.ucel_textem}</FormItem>
                  </FormRow>
                </div>
              </section>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...msg.sectionApplicant} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-u-vs-1 sg-a-mt-1'>
                  <FormRow>
                    <FormItem label={formatMessage(msg.jmeno)}>{data.zadatel?.jmeno}</FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.ico)}>{data.zadatel?.ico}</FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.adresa)}>{data.zadatel?.adresa}</FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.idSubjektu)}>
                      {data.zadatel?.id_subjektu}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.columnStatusZadatele)}>
                      {data.status_zadatele &&
                        formatMessage.lookup(statusZadateleMessages, data.status_zadatele)}
                    </FormItem>
                  </FormRow>
                </div>
              </section>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...msg.sectionDecision} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-u-vs-1 sg-a-mt-1'>
                  <FormRow>
                    <FormItem label={formatMessage(msg.approver)}>
                      {data.approver && (
                        <>
                          <div>
                            {`${data.approver.name ?? ''} ${data.approver.surname ?? ''}`.trim()}
                          </div>
                          {data.approver.email && (
                            <div className='sg-u-type-sml sg-a-mt-1/2'>
                              <Link href={`mailto:${data.approver.email}`} severity='colorful'>
                                {data.approver.email}
                              </Link>
                            </div>
                          )}
                        </>
                      )}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.decided)}>
                      {data.decided && (
                        <time dateTime={data.decided}>
                          <FormattedDate value={new Date(data.decided)} />{' '}
                          <FormattedTime value={new Date(data.decided)} />
                        </time>
                      )}
                    </FormItem>
                  </FormRow>
                  {data.rejection_reason && (
                    <FormRow>
                      <FormItem label={formatMessage(msg.rejectionReason)}>
                        {data.rejection_reason}
                      </FormItem>
                    </FormRow>
                  )}
                </div>
              </section>
            </div>

            <div className='sg-u-vs-4'>
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...msg.sectionFiles} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                  <section>
                    {data.has_priloha ? (
                      <DocDownloadFiles
                        title={<FormattedMessage {...msg.sectionFilesDocs} />}
                        docId={id}
                        items={[
                          {
                            permission: data.has_priloha,
                            link: prilohaLink,
                            label: data.priloha_nazev ?? formatMessage(msg.sectionAttachment),
                            ext: data.priloha_nazev?.split('.').pop(),
                          },
                        ]}
                      />
                    ) : (
                      <>
                        <h3 className='sg-u-type-h2 sg-a-ml-2'>
                          <FormattedMessage {...msg.sectionFilesDocs} />
                        </h3>
                        <div className='sg-a-mt-1'>
                          <InfoAlert>
                            <FormattedMessage {...msg.attachmentEmpty} />
                          </InfoAlert>
                        </div>
                      </>
                    )}
                  </section>
                </div>
              </section>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...msg.sectionAreas} />
                </h3>
                {areaFeatures.length > 0 && mapState ? (
                  <div className='sg-u-box sg-a-p-2 sg-u-vs-1 sg-a-mt-1'>
                    <MapWrapper>
                      <MapWithAreas state={mapState} features={areaFeatures} />
                    </MapWrapper>
                  </div>
                ) : (
                  <div className='sg-a-mt-1'>
                    <InfoAlert>
                      <FormattedMessage {...msg.areasEmpty} />
                    </InfoAlert>
                  </div>
                )}
              </section>
            </div>
          </div>
        )}

        {isApprovalDialogVisible && (
          <Dialog width='400px' onEscKeyDown={handleDialogClose} onOverlayClick={handleDialogClose}>
            <div className='sg-a-p-4 sg-u-vs-2'>
              <p>
                <FormattedMessage {...msg.approveDialogText} />
              </p>
              <BtnBar>
                <CtaBtn onClick={handleApprovalConfirm}>
                  <FormattedMessage {...msg.approveBtn} />
                </CtaBtn>
                <SecondaryBtn isDanger onClick={handleDialogClose}>
                  <FormattedMessage {...msg.closeDialogBtn} />
                </SecondaryBtn>
              </BtnBar>
            </div>
          </Dialog>
        )}
        {isRejectionDialogVisible && (
          <Dialog width='400px' onEscKeyDown={handleDialogClose} onOverlayClick={handleDialogClose}>
            <RejectionDialog
              confirmRejected={handleRejectionConfirm}
              closeRejectionDialog={handleDialogClose}
            />
          </Dialog>
        )}
      </article>
    </MainContent>
  );
};
